import './styles.scoped.css'
import {useState, useEffect} from 'react'
import {Typography, TextField, Button, IconButton, Menu, MenuItem, Skeleton} from '@mui/material'
import {useForm} from 'react-hook-form'
import {yupResolver} from '@hookform/resolvers/yup'
import * as yup from 'yup'
import {caregiverApi} from '../../../../services'
import {useParams} from 'react-router-dom'
import {useGlobalStates} from '../../../../contexts/GlobalStatesContext'
import {parseError} from '../../../../utils/helpers'
import {MoreHoriz} from '@mui/icons-material'
import {format} from 'date-fns'
import {SkeletonContainer, FieldSkeleton, ButtonSkeleton, TextSkeleton} from '../../../../components'

// The 8-row note editor, the actions and a short note history.
const AdditionalInfoSkeleton = () => (
    <SkeletonContainer>
        <FieldSkeleton labelWidth={75} height={194} />
        <br />
        <br />
        <div className="actions">
            <ButtonSkeleton width={80} />
            <ButtonSkeleton width={130} />
        </div>
        <br />
        <br />
        <TextSkeleton width={95} fontSize={12} />
        <br />
        {['85%', '60%'].map(width => (
            <div key={width}>
                <div className="note-card">
                    <div className="more">
                        <Skeleton variant="circular" width={24} height={24} sx={{display: 'inline-block', m: 1}} />
                    </div>
                    <TextSkeleton width={width} fontSize={12} />
                    <TextSkeleton width={190} fontSize={12} />
                </div>
                <br />
            </div>
        ))}
    </SkeletonContainer>
)

export default () => {
    const [anchorEl, setAnchorEl] = useState(null)
    const [selectedRow, setSelectedRow] = useState(null)
    const [notes, setNotes] = useState([])
    const {setLoading, setMessageBox, branchId} = useGlobalStates()
    const {caregiverId} = useParams()
    const [skeleton, setSkeleton] = useState(true)
    const form = useForm({
        resolver: yupResolver(
            yup.object().shape({
                internalNote: yup.string(),
            }),
        ),
    })

    useEffect(() => {
        reset(true)
    }, [])

    // The first load shows the skeleton; Reset and the reloads after a save or delete run under the global spinner.
    const reset = async (initial = false) => {
        try {
            if (!initial) setLoading(true)
            const response = await caregiverApi.getCaregiverNotes(branchId, caregiverId)
            const {data} = response
            const sortedNotes = data?.notes?.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
            form.setValue('internalNote', sortedNotes?.[0]?.notes)
            setNotes(sortedNotes)
            if (!initial) setLoading(false)
            setSkeleton(false)
        } catch (error) {
            if (!initial) setLoading(false)
            setSkeleton(false)
            const {title, description} = parseError(error)
            if (description) {
                setMessageBox({status: 'error', title, description})
            }
        }
    }

    const submit = async data => {
        if (data?.internalNote) {
            try {
                setLoading(true)
                await caregiverApi.createCaregiverNote(branchId, caregiverId, {notes: data?.internalNote})
                setMessageBox({
                    status: 'success',
                    title: 'Caregiver Informations Updated',
                    description: 'Internal Notes have been successfully updated. All changes are now reflected in the system.',
                })
                setLoading(false)
                reset()
            } catch (error) {
                setLoading(false)
                const {title, description} = parseError(error)
                if (description) {
                    setMessageBox({status: 'error', title, description})
                }
            }
        }
    }

    const menuOpen = (e, row) => {
        setAnchorEl(e.currentTarget)
        setSelectedRow(row)
    }

    const menuClose = () => {
        setAnchorEl(null)
    }

    const deleteNote = () => {
        menuClose()
        setLoading(true)
        caregiverApi
            .deleteCaregiverNote(branchId, caregiverId, selectedRow?.id)
            .then(() => {
                setLoading(false)
                setMessageBox({
                    status: 'success',
                    title: 'Client Notes Deleted',
                    description: 'Internal Notes has been deleted successfully.',
                })
                reset()
            })
            .catch(error => {
                setLoading(false)
                const {title, description} = parseError(error)
                if (description) {
                    setMessageBox({status: 'error', title, description})
                }
            })
    }

    return (
        <>
            {skeleton && <AdditionalInfoSkeleton />}
            <div style={{display: skeleton ? 'none' : 'block'}}>
                <Typography variant="inputLabel" color="text.secondary">
                    Internal Note
                </Typography>
                <TextField placeholder="Enter your internal note" multiline rows={8} fullWidth {...form.register('internalNote')} />
                <br />
                <br />
                <br />
                <div className="actions">
                    <Button variant="outlined" onClick={() => reset()}>
                        Reset
                    </Button>
                    <Button variant="contained" onClick={form.handleSubmit(submit)}>
                        Save Changes
                    </Button>
                </div>
                <br />
                <br />
                {notes && notes.length > 0 && (
                    <>
                        <Typography variant="overline" color="text.secondary">
                            NOTE HISTORY
                        </Typography>
                        <br />
                        <br />
                    </>
                )}
                {notes?.map(
                    (note, index) =>
                        index > 0 && (
                            <div key={index}>
                                <div className="note-card">
                                    <div className="more">
                                        <IconButton onClick={e => menuOpen(e, note)}>
                                            <MoreHoriz />
                                        </IconButton>
                                    </div>
                                    <Typography variant="caption" color="text.primary">
                                        {note?.notes}
                                    </Typography>
                                    <br />
                                    <Typography variant="caption" color="text.disabled">
                                        Added: {format(new Date(note?.created_at), 'MM/dd/yyyy')} by {note?.created_by}
                                    </Typography>
                                </div>
                                <br />
                            </div>
                        ),
                )}
            </div>

            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={menuClose}
                anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
                transformOrigin={{vertical: 'top', horizontal: 'right'}}>
                <div>
                    <MenuItem onClick={deleteNote} sx={theme => ({color: theme.palette.error.main})}>
                        Delete Note
                    </MenuItem>
                </div>
            </Menu>
        </>
    )
}
